/*
  Top down variant chooser for priced dropdowns (snippets/product-variant-picker,
  on variant pickers with picker_type dropdown and priced options on).

  Why: many buildings do not offer every combination (the Dove log cabin has no
  UPVC windows at 28mm), and prices are not additive (the 44mm upgrade costs
  more on bigger sizes). The stock picker greyed out choices depending on the
  order they were picked, and Shopify then guessed a nearest variant, silently
  changing other dropdowns.

  Rules, top dropdown first:
  - Dropdown 1 shows "from £x" per choice (cheapest available variant), or
    just "£x" when it is the only dropdown.
  - Later dropdowns show "+£x" against the cheapest valid choice, given the
    choices above them. Each figure is the cheapest way to complete the build
    with that choice, so from price plus each "+£" always adds up to the total
    even where upgrade prices vary by size. The cheapest shows no price.
  - Choices that cannot combine with those above, or are sold out, are greyed
    out with the reason. When nothing is available at all, sold out choices are
    labelled but left selectable so the page still shows the product.
  - Choices are ordered cheapest first (unavailable ones last, original order).
  - Changing a dropdown never changes the dropdowns above it. Below it, a choice
    is kept if the combination is available, otherwise it moves to the cheapest
    valid choice and a note under that dropdown says why.

  The full combination is settled here, in a capture phase change listener,
  before the theme's VariantSelects handler reads the selected option values
  and asks Shopify to re-render, so Shopify never has to guess. The changed
  select itself is not touched during the event (reordering its options there
  let browsers snap back to the first choice, so 44mm could not be picked on
  the Dove). The re-rendered
  picker is decorated through ProductInfo's preProcessHtmlCallbacks before it
  is swapped in, so the plain Liquid labels never flash.
*/
(() => {
  if (window.WaltonsVariantChooser) return;

  const notesByHost = new WeakMap();

  const money = (pence, pennies) => {
    const pounds = pence / 100;
    const decimals = pennies || !Number.isInteger(pounds) ? 2 : 0;
    return `£${pounds.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  function model(chooser) {
    const json = chooser.querySelector('script[data-chooser-variants]');
    if (!json) return null;
    let variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (e) {
      return null;
    }
    const selects = Array.from(chooser.querySelectorAll('select[name^="options["]'));
    if (!selects.length || !variants.length) return null;
    return { variants, selects, anyAvailable: variants.some((v) => v.available) };
  }

  const valuesOf = (select) => Array.from(select.options).map((o) => o.value);
  const matchesPrefix = (v, selections, upTo) => {
    for (let i = 0; i < upTo; i += 1) if (v.options[i] !== selections[i]) return false;
    return true;
  };
  const usable = (m, v) => !m.anyAvailable || v.available;

  function candidates(m, selections, index) {
    return m.variants.filter((v) => matchesPrefix(v, selections, index) && usable(m, v));
  }

  function cheapestValue(m, selections, index, originalOrder) {
    const byValue = new Map();
    candidates(m, selections, index).forEach((v) => {
      const value = v.options[index];
      byValue.set(value, Math.min(byValue.has(value) ? byValue.get(value) : Infinity, v.price));
    });
    let best = null;
    originalOrder.forEach((value) => {
      if (!byValue.has(value)) return;
      if (best === null || byValue.get(value) < byValue.get(best)) best = value;
    });
    return best;
  }

  function resolve(m, selections, changedIndex) {
    const next = selections.slice();
    const changes = [];
    for (let i = changedIndex + 1; i < m.selects.length; i += 1) {
      const valid = new Set(candidates(m, next, i).map((v) => v.options[i]));
      if (valid.has(next[i])) continue;
      const replacement = cheapestValue(m, next, i, valuesOf(m.selects[i]));
      if (replacement === null) break;
      changes.push({ index: i, from: next[i], to: replacement });
      next[i] = replacement;
    }
    return { selections: next, changes };
  }

  function blockerFor(m, selections, index, value) {
    for (let j = 0; j < index; j += 1) {
      const exists = m.variants.some((v) => matchesPrefix(v, selections, j + 1) && v.options[index] === value);
      if (!exists) return selections[j];
    }
    return null;
  }

  function decorate(chooser, notes) {
    const m = model(chooser);
    if (!m) return;
    const selections = m.selects.map((s) => (s.querySelector('option[selected]') || s.options[s.selectedIndex] || {}).value);

    m.selects.forEach((select, index) => {
      const selectedValue = selections[index];
      const rows = Array.from(select.options).map((option, order) => {
        const value = option.value;
        const all = m.variants.filter((v) => matchesPrefix(v, selections, index) && v.options[index] === value);
        const row = { option, order, value, state: 'ok', label: value, key: Infinity };
        if (!all.length) {
          const blocker = blockerFor(m, selections, index, value);
          row.state = 'none';
          row.label = blocker ? `${value}, not available with ${blocker}` : `${value}, not available`;
          return row;
        }
        const available = all.filter((v) => v.available);
        if (!available.length && m.anyAvailable) {
          row.state = 'soldout';
          row.label = `${value}, sold out`;
          return row;
        }
        row.key = Math.min(...(m.anyAvailable ? available : all).map((v) => v.price));
        if (index === 0) row.label = m.selects.length > 1 ? `${value}, from ${money(row.key, true)}` : `${value}, ${money(row.key, true)}`;
        return row;
      });

      if (index > 0) {
        const base = Math.min(...rows.filter((r) => r.state === 'ok').map((r) => r.key));
        rows.forEach((r) => {
          if (r.state === 'ok' && r.key > base) r.label = `${r.value} +${money(r.key - base)}`;
        });
      }

      rows
        .slice()
        .sort((a, b) => {
          const aOk = a.state === 'ok' ? 0 : 1;
          const bOk = b.state === 'ok' ? 0 : 1;
          return aOk - bOk || a.key - b.key || a.order - b.order;
        })
        .forEach((r, position) => {
          r.option.textContent = r.label;
          r.option.disabled = r.state !== 'ok' && r.value !== selectedValue;
          if (select.options[position] !== r.option) select.insertBefore(r.option, select.options[position] || null);
        });

      // Moving an option can make a browser fall back to its first choice;
      // put the selection back on the chosen value.
      Array.from(select.options).forEach((o) => {
        if (o.value === selectedValue) o.selected = true;
      });

      const wrapper = select.closest('.variant-input-wrapper') || select.parentElement;
      wrapper.querySelectorAll('.variant-chooser__note').forEach((n) => n.remove());
      const note = (notes || []).find((n) => n.index === index);
      if (note) {
        const p = wrapper.ownerDocument.createElement('p');
        p.className = 'variant-chooser__note';
        p.setAttribute('role', 'status');
        p.textContent = note.text;
        wrapper.appendChild(p);
      }
    });
  }

  function hostOf(chooser) {
    return chooser.closest('product-info') || chooser.parentElement;
  }

  function onChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const chooser = select.closest('variant-selects[data-chooser]');
    if (!chooser) return;
    const m = model(chooser);
    if (!m) return;
    const index = m.selects.indexOf(select);
    if (index === -1) return;

    const selections = m.selects.map((s, i) => (i === index ? s.value : (s.querySelector('option[selected]') || s.options[s.selectedIndex] || {}).value));
    const { selections: settled, changes } = resolve(m, selections, index);

    // Only the dropdowns below the changed one are touched here. The changed
    // select is left exactly as the customer set it, because the theme's own
    // handler reads its selectedOptions[0] right after this listener.
    changes.forEach(({ index: i, to }) => {
      const target = m.selects[i];
      Array.from(target.options).forEach((o) => {
        if (o.value === to) {
          o.setAttribute('selected', '');
          o.selected = true;
        } else {
          o.removeAttribute('selected');
        }
      });
    });

    const notes = changes.map(({ index: i, from, to }) => {
      const exists = m.variants.some((v) => matchesPrefix(v, settled, i) && v.options[i] === from);
      const reason = exists ? 'is sold out' : 'is not available';
      return { index: i, text: `${from} ${reason} with ${settled[index]}, so we have switched to ${to}.` };
    });
    notesByHost.set(hostOf(chooser), notes);

    // Relabel once the theme's handler (later in this same event) has read the
    // chosen values, so the new labels show at once instead of popping in when
    // Shopify's re-render arrives. The re-render is decorated identically
    // through preProcessHtmlCallbacks, so the swap is invisible.
    setTimeout(() => {
      if (chooser.isConnected) decorate(chooser, notes);
    }, 0);
  }

  function hook(chooser) {
    const host = chooser.closest('product-info');
    if (!host || host.__variantChooserHooked || !Array.isArray(host.preProcessHtmlCallbacks)) return;
    host.__variantChooserHooked = true;
    host.preProcessHtmlCallbacks.push((newContent) => {
      if (newContent && newContent.matches && newContent.matches('variant-selects[data-chooser]')) {
        decorate(newContent, notesByHost.get(host));
      }
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('variant-selects[data-chooser]').forEach((chooser) => {
      hook(chooser);
      decorate(chooser, notesByHost.get(hostOf(chooser)));
    });
  }

  document.addEventListener('change', onChange, true);
  window.WaltonsVariantChooser = { init, resolve, decorate };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
